// Visit room — opens the real Zoom meeting (native app / browser). The member joins the scheduled
// meeting and sees the visit title + time + "waiting for the host" until the clinician starts it.
// Zoom Meetings only — the sole supported video path (D1). The host start_url is never exposed here.
import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Btn, Surface } from "@championcare/ui";
import { ApiError } from "@forcebase-ai/api-client";
import { useApi } from "../api";
import type { JoinTicket } from "@forcebase-ai/schema/patient";

type Phase = "loading" | "ready" | "waiting" | "error";

// Mirror the server join window (zoom/window.ts OPEN_BEFORE_MS): the room opens 10 min early.
const JOIN_OPENS_BEFORE_MS = 10 * 60 * 1000;

function formatCountdown(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const days = Math.floor(total / 86400);
  const hours = Math.floor((total % 86400) / 3600);
  const mins = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${mins}m`;
  if (mins > 0) return `${mins}m ${String(secs).padStart(2, "0")}s`;
  return `${secs}s`;
}

export function VisitRoom() {
  const api = useApi();
  const aliveRef = useRef(true);
  const [phase, setPhase] = useState<Phase>("loading");
  const [ticket, setTicket] = useState<JoinTicket | null>(null);
  const [message, setMessage] = useState<string>("");
  const [canRetry, setCanRetry] = useState(false);
  const [opensAt, setOpensAt] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const idRef = useRef<string>(window.location.pathname.split("/")[2] ?? "");

  const connect = useCallback(async () => {
    setPhase("loading");
    setMessage("");
    setCanRetry(false);
    try {
      const t = await api.joinVisit(idRef.current);
      if (!aliveRef.current) return;
      setTicket(t);
      setPhase("ready");
    } catch (e) {
      if (!aliveRef.current) return;
      const code = e instanceof ApiError ? e.code : undefined;
      if (code === "out_of_window") {
        // Too early — look up the start time and show a live countdown to when the room opens.
        const appts = await api.listAppointments().catch(() => null);
        const appt = appts?.find((a) => a.id === idRef.current);
        if (appt && aliveRef.current) {
          setOpensAt(new Date(appt.scheduled_at).getTime() - JOIN_OPENS_BEFORE_MS);
          setNow(Date.now());
          setPhase("waiting");
          return;
        }
      }
      setMessage(
        code === "not_ready"
          ? "Your secure video room is still being set up. This usually takes a few seconds."
          : e instanceof ApiError
            ? e.message
            : "Could not open the visit.",
      );
      setCanRetry(code !== "out_of_window");
      setPhase("error");
    }
  }, [api]);

  useEffect(() => {
    aliveRef.current = true;
    void connect();
    return () => {
      aliveRef.current = false;
    };
  }, [connect]);

  // While waiting, tick once a second and reconnect the moment the window opens — no refresh.
  useEffect(() => {
    if (phase !== "waiting" || opensAt === null) return;
    const timer = setInterval(() => {
      const n = Date.now();
      setNow(n);
      if (n >= opensAt) {
        clearInterval(timer);
        void connect();
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [phase, opensAt, connect]);

  return (
    <div className="page">
      <div className="app-page-head">
        <h1>Your <em>visit.</em></h1>
      </div>

      {phase === "loading" && (
        <Surface className="card vr-connecting">
          <span className="vr-spinner" aria-hidden />
          <div>
            <p className="vr-connecting__title">Preparing your secure room</p>
            <p className="card__hint" style={{ margin: 0 }}>
              This only takes a moment.
            </p>
          </div>
        </Surface>
      )}

      {phase === "waiting" && opensAt !== null && (
        <Surface className="card vr-waiting">
          <p className="vr-waiting__eyebrow">Waiting room</p>
          <p className="vr-waiting__count">{formatCountdown(opensAt - now)}</p>
          <p className="card__hint" style={{ margin: 0 }}>
            Your room opens 10 minutes before your appointment. We&rsquo;ll bring you in
            automatically — no need to refresh.
          </p>
        </Surface>
      )}

      {phase === "ready" && ticket && (
        <Surface className="card vr-ready">
          <p className="vr-ready__title">You&rsquo;re ready to join</p>
          <p className="card__hint">
            Tap below to open your secure Zoom visit. You&rsquo;ll see the visit details and wait for
            your clinician to start — they&rsquo;ll admit you when it&rsquo;s time.
          </p>
          {ticket.joinUrl ? (
            <Btn as="a" variant="primary" href={ticket.joinUrl} target="_blank" rel="noreferrer">
              Join visit in Zoom
            </Btn>
          ) : (
            <p>Join link unavailable — please contact support.</p>
          )}
          {ticket.passcode && (
            <p className="card__hint" style={{ marginBottom: 0 }}>
              Passcode (if asked): <strong>{ticket.passcode}</strong>
            </p>
          )}
        </Surface>
      )}

      {phase === "error" && (
        <Surface className="card">
          <p className="card__hint">{message}</p>
          {canRetry && (
            <Btn variant="primary" onClick={() => void connect()}>
              Try again
            </Btn>
          )}
        </Surface>
      )}

      <Link to="/visits" className="back-link">
        ← Visit details
      </Link>
    </div>
  );
}
